import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Collapse,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    MenuItem,
    Paper,
    Tooltip,
    Typography
} from "@mui/material";
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DeleteIcon from '@mui/icons-material/Delete';
import { useHistory } from 'react-router-dom';

import CustomerAgentApi from '../../../../api/customer-agent/customer-agent.api';
import AutocompleteSelect from '../../../../component/select/autoCompleteSelect';
import FilterSelect from '../../../../component/select/filterSelect';
import ButtonCustom from '../../../../component/button/buttonCustom';
import { swalActive, swalError, swalSuccess } from '../../../../component/notification/swal';
import { colors } from '../../../../constants/colors';
import {
    CUSTOMER_AGENT_TYPE_LABEL,
    CUSTOMER_AGENT_TYPE_OPTIONS,
    HEIR_STATUS,
    canManageHeir as isHeirManageable,
    customerAgentTypeColor,
    customerAgentTypeLabel
} from '../../../../constants/customerAgent';
import { routeName } from '../../../../routes/routers-name';
import { dateTimeToView, dateToView } from '../../../../utils/date.utils';
import { escapeHtml } from '../../../../utils/app.utils';
import moment from 'moment';

export type CustomerAgentItem = {
    id_customer_agent: number;
    customerId_agent?: number;
    customer_fullname?: string;
    customer_agent_mobilePhone?: string;
    /** ชื่อผู้สร้าง (คำนำหน้า+ชื่อ+นามสกุล) */
    created_name?: string;
    created_date?: string;
    /** PAYER | HEIR | ALL */
    agent_type?: string;
    /** ACTIVE | EXPIRED | DOC_RECEIVED | null (ไม่ใช่ผู้รับมรดก) */
    heir_status?: string | null;
    /** YYYY-MM-DD วันที่เริ่มหมดอายุ (ใช้ได้ถึงวันก่อนหน้า) */
    heir_expire_date?: string | null;
    doc_received_date?: string | null;
}

type Props = {
    id_customer: number;
    /** userId ของเจ้าของ (customer ที่ admin กำลังดู) ใช้เป็น body.userId ตอน POST */
    id_user?: number | null;
    onDelete?: (id_customer_agent: number) => void;
    numRefresh?: number;
    /** ผูกกับ permission ฝั่ง parent — ถ้า false จะซ่อนปุ่ม/ฟอร์มเพิ่มผู้แทน */
    canAdd?: boolean;
    /** ผูกกับ permission ฝั่ง parent — ถ้า false จะซ่อนปุ่ม ต่ออายุ / รับเอกสาร */
    canManageHeir?: boolean;
    /** เรียกหลังต่ออายุ/รับเอกสารสำเร็จ ให้ parent โหลดตารางใหม่ (คอลัมน์แจ้งเตือน) */
    onHeirChanged?: () => void;
    /** หลังรับเอกสารสำเร็จ — ถ้าส่งมา parent เป็นคนแจ้งผลเอง (เช่น ถามว่าจะไปทำรายการโอนมรดกต่อไหม)
     *  modal จะไม่แสดง swal สำเร็จซ้อนอีกตัว */
    onReceiveDocSuccess?: (item: CustomerAgentItem) => void;
}

const initStateErrorMessage = {
    CUSTOMER_AGENT: ``,
    AGENT_TYPE: ``,
}

/** PDPA: โชว์เลขบัตรประชาชนแค่ 3 หลักท้าย พอให้แยกคนชื่อซ้ำ ไม่ต้องเห็นเต็ม */
const maskIdCard = (idCard?: string) => {
    const digits = String(idCard ?? '').replace(/\D/g, '');
    if (digits.length < 3) return '-';
    const tail = digits.slice(-3);
    return `x-xxxx-xxxxx-${tail.slice(0, 2)}-${tail.slice(2)}`;
}

const personFullName = (person: any) =>
    `${person?.firstname || ''} ${person?.lastname || ''}`.trim();

const ModalAgent: React.FC<Props> = ({ id_customer, id_user, onDelete, numRefresh, canAdd = true, canManageHeir = true, onHeirChanged, onReceiveDocSuccess }: Props) => {
    const history = useHistory();

    const [data, set_data] = useState<CustomerAgentItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    /** ฟอร์มเพิ่มผู้แทน */
    const [isOpenAdd, setIsOpenAdd] = useState<boolean>(false);
    const [personOptions, setPersonOptions] = useState<any[]>([]);
    const [isLoadingPerson, setIsLoadingPerson] = useState<boolean>(false);
    const [searchPerson, setSearchPerson] = useState<string>('');
    const [formPerson, setFormPerson] = useState<any>(null);
    const [formAgentType, setFormAgentType] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<any>(initStateErrorMessage);
    /** แถวที่กำลังยิง ต่ออายุ/รับเอกสาร อยู่ กันกดซ้ำ */
    const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);

    const clearErrorMessage = () => setErrorMessage(initStateErrorMessage);

    const handleGetData = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await CustomerAgentApi.listCustomerNew({
                page: 1,
                limit: 100,
                sortBy: "createDate",
                sortType: "DESC",
                customerId: id_customer
            });
            if (res.status < 300) {
                const response = (res.data as any)?.data;
                set_data(response?.items || []);
            } else {
                set_data([]);
            }
        } catch (error) {
            set_data([]);
        } finally {
            setIsLoading(false);
        }
    }, [id_customer])

    useEffect(() => {
        handleGetData();
    }, [handleGetData, numRefresh])

    /** ดึงรายชื่อคนสำหรับ dropdown เลือกผู้แทน */
    const loadPersonOptions = useCallback(async (keyword: string) => {
        setIsLoadingPerson(true);
        try {
            let condition: any = { page: 1, pageLimit: 100 };
            if (keyword) condition = { ...condition, keyword };

            const res = await CustomerAgentApi.search_customer(condition);
            if (res?.data?.status < 300) {
                setPersonOptions(res?.data?.data?.items || []);
            } else {
                setPersonOptions([]);
            }
        } catch (error) {
            setPersonOptions([]);
        } finally {
            setIsLoadingPerson(false);
        }
    }, [])

    /** debounce 400ms กันยิง search ทุก keystroke */
    useEffect(() => {
        if (!isOpenAdd) return;
        const timer = setTimeout(() => { loadPersonOptions(searchPerson) }, 400);
        return () => clearTimeout(timer);
    }, [isOpenAdd, searchPerson, loadPersonOptions])

    /** ตัดคนที่เป็นผู้แทนอยู่แล้ว และตัวเจ้าของเองออกจาก dropdown — กันเพิ่มซ้ำตั้งแต่ต้นทาง */
    const personOptionsFiltered = useMemo(() => {
        const usedCustomerIds = data.map((item) => Number(item.customerId_agent));
        return personOptions.filter((option: any) =>
            Number(option?.customerId) !== Number(id_customer) &&
            !usedCustomerIds.includes(Number(option?.customerId))
        );
    }, [personOptions, data, id_customer])

    const resetForm = () => {
        setFormPerson(null);
        setFormAgentType('');
        setSearchPerson('');
        clearErrorMessage();
    }

    const handleToggleAdd = () => {
        if (isOpenAdd) resetForm();
        setIsOpenAdd(!isOpenAdd);
    }

    const handleSubmit = async () => {
        if (!formPerson) {
            return setErrorMessage({ ...errorMessage, ...{ CUSTOMER_AGENT: 'กรุณาเลือกผู้ทำการแทน' } });
        }
        if (!formAgentType) {
            return setErrorMessage({ ...errorMessage, ...{ AGENT_TYPE: 'กรุณาเลือกประเภทผู้ทำการแทน' } });
        }

        const body = {
            userId_agent: formPerson?.userId,
            customerId_agent: formPerson?.customerId,
            customerId: id_customer,
            userId: id_user,
            status: "ACTIVE",
            agent_type: formAgentType,
        }

        setIsSubmitting(true);
        await CustomerAgentApi.post(body)
            .then(async (res) => {
                if (res.status < 300) {
                    swalSuccess('เพิ่มผู้ทำการแทนเรียบร้อย');
                    resetForm();
                    setIsOpenAdd(false);
                    await handleGetData();
                }
            })
            .catch((error) => {
                swalError(error?.response?.data?.error_description || 'เพิ่มผู้ทำการแทน ไม่สำเร็จ');
            })
            .finally(() => setIsSubmitting(false))
    }

    const runHeirAction = (
        item: CustomerAgentItem,
        confirm: { title: string; html: string; confirmText: string },
        request: (id: number) => Promise<any>,
        message: { success: string; error: string },
        /** ถ้าส่งมา จะเรียกแทน swalSuccess — swal เปิดได้ทีละตัว ตัวหลังจะทับตัวแรกทันที */
        onSuccess?: () => void
    ) => {
        swalActive(confirm.title, confirm.html, async (status: boolean) => {
            if (!status) return;
            setActionLoadingId(item.id_customer_agent);
            try {
                const res = await request(item.id_customer_agent);
                if (res.status < 300) {
                    handleGetData();
                    onHeirChanged?.();
                    if (onSuccess) onSuccess();
                    else swalSuccess(message.success);
                }
            } catch (error: any) {
                swalError(error?.response?.data?.error_description || message.error);
            } finally {
                setActionLoadingId(null);
            }
        }, 'question', confirm.confirmText)
    }

    const handleRenew = (item: CustomerAgentItem) =>
        runHeirAction(
            item,
            {
                title: 'ต้องการต่ออายุผู้รับมรดกใช่หรือไม่?',
                html: `${escapeHtml(item.customer_fullname)}<br/>ต่ออายุอีก 30 วัน นับจากวันนี้`,
                confirmText: 'ต่ออายุ'
            },
            (id) => CustomerAgentApi.renew(id),
            { success: 'ต่ออายุผู้รับมรดกเรียบร้อย', error: 'ต่ออายุผู้รับมรดก ไม่สำเร็จ' }
        )

    const handleReceiveDoc = (item: CustomerAgentItem) =>
        runHeirAction(
            item,
            {
                title: 'ยืนยันการรับเอกสารผู้รับมรดก',
                html: `${escapeHtml(item.customer_fullname)}<br/>เมื่อรับเอกสารแล้ว ผู้รับมรดกจะใช้งานได้ถาวร`,
                confirmText: 'รับเอกสาร'
            },
            (id) => CustomerAgentApi.receiveDoc(id),
            { success: 'รับเอกสารเรียบร้อย', error: 'รับเอกสาร ไม่สำเร็จ' },
            onReceiveDocSuccess ? () => onReceiveDocSuccess(item) : undefined
        )

    /**
     * หมดอายุแล้วหรือยัง — heir_expire_date คือวันที่เริ่มหมดอายุ (00:00 ของวันนั้น)
     * เช็ควันที่ด้วย ไม่ดูแค่ heir_status เพราะ CRON อัปเดตสถานะตอนเที่ยงคืน ช่วงก่อน CRON รันสถานะยังเป็น ACTIVE
     */
    const isHeirExpired = (item: CustomerAgentItem) =>
        item.heir_status === HEIR_STATUS.EXPIRED ||
        (!!item.heir_expire_date && !moment().isBefore(moment(item.heir_expire_date, 'YYYY-MM-DD'), 'day'));

    const renderHeirStatus = (item: CustomerAgentItem) => {
        if (item.heir_status === HEIR_STATUS.ACTIVE) {
            /** heir_expire_date คือวันที่เริ่มหมดอายุ (00:00) — วันสุดท้ายที่ใช้ได้คือวันก่อนหน้า */
            const lastDay = item.heir_expire_date ? dateToView(moment(item.heir_expire_date).subtract(1, 'day').toDate()) : '-';
            return <Typography sx={{ fontSize: 14, color: '#ED6C02' }}>สิทธิ์ชั่วคราว ใช้งานได้ถึง {lastDay}</Typography>
        }
        if (item.heir_status === HEIR_STATUS.EXPIRED) {
            return (
                <Typography sx={{ fontSize: 14, color: colors.red }}>
                    สิทธิ์ชั่วคราวหมดอายุแล้ว{item.heir_expire_date ? ` (ตั้งแต่ ${dateToView(item.heir_expire_date)})` : ''}
                </Typography>
            )
        }
        if (item.heir_status === HEIR_STATUS.DOC_RECEIVED) {
            return (
                <Typography sx={{ fontSize: 14, color: colors.green }}>
                    รับเอกสารแล้ว{item.doc_received_date ? ` • ${dateTimeToView(item.doc_received_date)}` : ''}
                </Typography>
            )
        }
        return null
    }

    const renderAgentTypeBadge = (type?: string) => (
        <Box
            component="span"
            sx={{
                display: 'inline-block',
                px: 1.5,
                py: 0.25,
                borderRadius: '4px',
                fontSize: 14,
                color: colors.white,
                backgroundColor: customerAgentTypeColor(type),
                whiteSpace: 'nowrap'
            }}
        >
            {customerAgentTypeLabel(type)}
        </Box>
    )

    return (
        <Box sx={{ my: 2 }}>
            {/* หัวข้อ + ปุ่มเพิ่มผู้แทน */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
            >
                <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                    ผู้ทำการแทนทั้งหมด {data?.length || 0} รายการ
                </Typography>
                {canAdd && (
                    <ButtonCustom
                        mode="add"
                        textButton={isOpenAdd ? 'ปิดฟอร์มเพิ่มผู้แทน' : 'เพิ่มผู้แทน'}
                        onClick={handleToggleAdd}
                        style={{ margin: 0 }}
                        btnStyle={{ fontSize: '16px' }}
                    />
                )}
            </Box>

            {/* ฟอร์มเพิ่มผู้แทน */}
            {canAdd && (
                <Collapse in={isOpenAdd} unmountOnExit>
                    <Paper elevation={0} sx={{ p: 2, mt: 2, backgroundColor: colors.bgBase, border: `1px solid ${colors.borderInput}` }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <AutocompleteSelect
                                    labelId="customer-agent-person"
                                    heading="เลือกผู้แทน"
                                    required
                                    placeholder="ค้นหาชื่อ หรือเลขบัตรประชาชน"
                                    options={personOptionsFiltered}
                                    getOptionLabel={(option: any) => personFullName(option)}
                                    // client-side filter ของ wrapper ใช้ getOptionLabel เป็นค่า default
                                    // ถ้าไม่ระบุ การค้นด้วยเลขบัตร (server หาเจอ) จะถูกกรองทิ้งฝั่ง client
                                    filterOptions={(option: any) => `${personFullName(option)} ${option?.idCard || ''}`}
                                    renderOption={(optionProps: any, option: any) => (
                                        <li {...optionProps} key={option?.customerId}>
                                            <Box>
                                                <Typography sx={{ fontSize: 16 }}>{personFullName(option) || '-'}</Typography>
                                                <Typography sx={{ fontSize: 14, color: colors.textLightGray }}>
                                                    เลขบัตรประชาชน : {maskIdCard(option?.idCard)}
                                                </Typography>
                                            </Box>
                                        </li>
                                    )}
                                    isOptionEqualToValue={(option: any, value: any) => option?.customerId === value?.customerId}
                                    inputValue={searchPerson || ''}
                                    onInputChange={(e, val) => setSearchPerson(val)}
                                    value={formPerson || null}
                                    onChange={(e, value) => {
                                        setFormPerson(value);
                                        clearErrorMessage();
                                    }}
                                    loading={isLoadingPerson}
                                    noOptionsText="ไม่พบรายชื่อ"
                                    helperText={errorMessage.CUSTOMER_AGENT}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FilterSelect
                                    labelId="label-customer-agent-type"
                                    selectId="select-customer-agent-type"
                                    heading="ประเภทผู้ทำการแทน"
                                    required
                                    value={formAgentType}
                                    onchange={(event: any) => {
                                        setFormAgentType(event.target.value);
                                        clearErrorMessage();
                                    }}
                                    options={[
                                        <MenuItem key="placeholder" value="" disabled>
                                            เลือกประเภทผู้ทำการแทน
                                        </MenuItem>,
                                        ...CUSTOMER_AGENT_TYPE_OPTIONS.map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {CUSTOMER_AGENT_TYPE_LABEL[type]}
                                            </MenuItem>
                                        ))
                                    ]}
                                    helperText={errorMessage.AGENT_TYPE}
                                />
                            </Grid>

                            {/* การ์ดสรุปคนที่เลือก (read-only) */}
                            {formPerson && (
                                <Grid item xs={12}>
                                    <Paper elevation={0} sx={{ p: 2, backgroundColor: colors.white, border: `1px dashed ${colors.borderInput}` }}>
                                        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                                            {personFullName(formPerson) || '-'}
                                        </Typography>
                                        <Box display="flex" flexWrap="wrap" gap={{ xs: 0, md: 3 }} flexDirection={{ xs: 'column', md: 'row' }}>
                                            <Typography sx={{ fontSize: 14, color: colors.textLightGray }}>
                                                เลขบัตรประชาชน : {maskIdCard(formPerson?.idCard)}
                                            </Typography>
                                            <Typography sx={{ fontSize: 14, color: colors.textLightGray }}>
                                                เบอร์โทร : {formPerson?.mobilePhone || '-'}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="end" gap={2} flexWrap="wrap">
                                    <ButtonCustom
                                        mode="del"
                                        textButton="ย้อนกลับ"
                                        onClick={() => { resetForm(); setIsOpenAdd(false); }}
                                        disabled={isSubmitting}
                                        style={{ margin: 0 }}
                                        btnStyle={{ fontSize: '16px', minWidth: 120 }}
                                    />
                                    <ButtonCustom
                                        textButton="ยืนยัน"
                                        onClick={handleSubmit}
                                        disabled={isSubmitting}
                                        style={{ margin: 0 }}
                                        btnStyle={{ fontSize: '16px', minWidth: 120 }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Collapse>
            )}

            <Divider sx={{ mt: 2 }} />

            {/* รายการผู้ทำการแทน */}
            <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
                        <CircularProgress size={32} sx={{ color: colors.themeMainColor }} />
                    </Box>
                ) : data?.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
                        <Typography sx={{ color: colors.textLightGray }}>ไม่พบข้อมูล...</Typography>
                    </Box>
                ) : (
                    <List disablePadding>
                        {data?.map((item: CustomerAgentItem, index: number) => (
                            <ListItem
                                key={item.id_customer_agent}
                                divider={data?.length - 1 !== index}
                                sx={{
                                    py: 1.5,
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    alignItems: { xs: 'flex-start', md: 'center' },
                                    gap: 1
                                }}
                            >
                                <Box flex={1} minWidth={0}>
                                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={0.5}>
                                        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                                            {item.customer_fullname || '-'}
                                        </Typography>
                                        {renderAgentTypeBadge(item.agent_type)}
                                    </Box>
                                    <Typography sx={{ fontSize: 14, color: colors.textLightGray }}>
                                        เบอร์โทร : {item.customer_agent_mobilePhone || '-'}
                                    </Typography>
                                    <Typography sx={{ fontSize: 14, color: colors.textLightGray }}>
                                        ผู้เพิ่ม : {item.created_name || '-'}
                                        {item.created_date ? ` • ${dateTimeToView(item.created_date)}` : ''}
                                    </Typography>
                                    {renderHeirStatus(item)}
                                </Box>
                                <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                                    {canManageHeir && isHeirManageable(item.heir_status) && (
                                        <>
                                            {/* ต่ออายุ: ไม่มีวันหมดอายุ = ไม่มีปุ่ม, ยังไม่หมดอายุ = ปุ่มจาง, หมดอายุแล้วถึงกดได้ */}
                                            {item.heir_expire_date && (
                                                <Tooltip
                                                    placement="top"
                                                    title={isHeirExpired(item) ? '' : 'ต่ออายุได้เมื่อสิทธิ์ชั่วคราวหมดอายุแล้ว'}
                                                >
                                                    {/* ปุ่มที่ disabled ไม่ส่ง mouse event ต้องห่อ span ให้ Tooltip ทำงาน */}
                                                    <span>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            disabled={actionLoadingId === item.id_customer_agent || !isHeirExpired(item)}
                                                            onClick={() => handleRenew(item)}
                                                            sx={{
                                                                fontSize: 14,
                                                                whiteSpace: 'nowrap',
                                                                color: colors.themeSecondColor,
                                                                borderColor: colors.themeSecondColor,
                                                                '&:hover': { borderColor: colors.themeSecondColor }
                                                            }}
                                                        >
                                                            ต่ออายุ
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            <Button
                                                variant="contained"
                                                size="small"
                                                disabled={actionLoadingId === item.id_customer_agent}
                                                onClick={() => handleReceiveDoc(item)}
                                                sx={{
                                                    fontSize: 14,
                                                    whiteSpace: 'nowrap',
                                                    boxShadow: 'none',
                                                    backgroundColor: colors.themeSecondColor,
                                                    '&:hover': { backgroundColor: colors.themeSecondColor, boxShadow: 'none', opacity: 0.9 }
                                                }}
                                            >
                                                รับเอกสาร
                                            </Button>
                                        </>
                                    )}
                                    <Tooltip title="ดูข้อมูลผู้ทำการแทน" placement="top">
                                        <IconButton
                                            sx={{ maxWidth: "fit-content" }}
                                            onClick={() => {
                                                history.push(`${routeName.agentProfile}/${item?.id_customer_agent}`, {
                                                    isAdmin: true
                                                })
                                            }}
                                        >
                                            <RemoveRedEyeIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="ลบตัวแทน" placement="top">
                                        <IconButton
                                            sx={{ maxWidth: "fit-content" }}
                                            onClick={() => onDelete?.(item?.id_customer_agent)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Paper>
        </Box>
    )
}

export default ModalAgent
